import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Lock,
  ShieldCheck,
  Users,
  Eye,
  Share2,
  Link2,
  FileDown,
  Monitor,
  Smartphone,
  Tablet,
  HardDrive,
  Globe,
  RefreshCw,
  LogOut,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const TOKEN_KEY = 'switchr-admin-token';
const INK = '#111418';
const ACCENT = '#0B5FFF';

const DEVICE_LABELS = { desktop: 'Desktop', mobile: 'Mobil', tablet: 'Tablet', bot: 'Bots', other: 'Sonstige' };
const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet, other: HardDrive, bot: HardDrive };

const regionName = (code) => {
  if (!code || code === 'XX') return 'Unbekannt';
  try {
    return new Intl.DisplayNames(['de'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

const nf = (n) => new Intl.NumberFormat('de-DE').format(Number(n || 0));

// ---- Login screen ----------------------------------------------------------
const LoginScreen = ({ onAuthed }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password || busy) return;
    setBusy(true);
    setError('');
    try {
      const r = await api.post('/admin/login', { password });
      const token = r?.data?.token;
      if (token) {
        try {
          sessionStorage.setItem(TOKEN_KEY, token);
        } catch {
          /* ignore */
        }
        onAuthed(token);
      } else {
        setError('Anmeldung fehlgeschlagen.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setError('Falsches Passwort.');
      else if (status === 503) setError('Admin-Bereich ist nicht konfiguriert (ADMIN_PASSWORD fehlt).');
      else setError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-[#0F1115] px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#151922] p-7 shadow-2xl">
        <div className="mb-5 flex flex-col items-center gap-2 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
            <Lock className="h-6 w-6 text-[#6EA8FF]" />
          </div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6EA8FF]">SwitchR</p>
          <h1 className="text-lg font-semibold text-white">Metriken · Admin</h1>
          <p className="text-sm text-white/55">Geschützter Bereich. Bitte Passwort eingeben.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="admin-pw" className="text-white/70">Passwort</Label>
          <Input
            id="admin-pw"
            type="password"
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border-white/15 bg-white/5 text-white placeholder:text-white/30"
            placeholder="••••••••"
            data-testid="admin-password-input"
          />
        </div>
        {error && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-[#F87171]/30 bg-[#F87171]/10 px-3 py-2 text-sm text-[#FCA5A5]" data-testid="admin-login-error">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={busy || !password}
          className="mt-5 h-10 w-full bg-[#0B5FFF] hover:bg-[#0a55e6]"
          data-testid="admin-login-button"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Anmelden
        </Button>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-[11px] text-white/40">
          <ShieldCheck className="h-3.5 w-3.5" /> Cookiefrei · nur anonyme Aggregatdaten
        </p>
      </form>
    </div>
  );
};

// ---- Dashboard building blocks ---------------------------------------------
const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <Card className="border-border/70" data-testid={testid}>
    <CardContent className="flex items-center gap-3.5 p-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: 'rgba(11,95,255,0.08)' }}>
        <Icon className="h-5 w-5" style={{ color: ACCENT }} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold tabular-nums text-foreground" data-testid={`${testid}-value`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </CardContent>
  </Card>
);

const BarList = ({ title, icon: Icon, rows, empty }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card className="border-border/70">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="h-4 w-4 text-muted-foreground" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-1">
        {rows.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">{empty}</p>
        ) : (
          <div className="space-y-2.5">
            {rows.map((r) => (
              <div key={r.key} data-testid={`barrow-${r.key}`}>
                <div className="mb-1 flex items-center justify-between text-[13px]">
                  <span className="flex items-center gap-1.5 text-foreground/80">
                    {r.leadIcon ? <r.leadIcon className="h-3.5 w-3.5 text-muted-foreground" /> : null}
                    {r.label}
                  </span>
                  <span className="font-medium tabular-nums text-foreground">{nf(r.count)}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: ACCENT }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-md border border-border bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-foreground">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium tabular-nums text-foreground">{nf(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ---- Dashboard --------------------------------------------------------------
const Dashboard = ({ token, onLogout }) => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/admin/stats?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        onLogout();
        return;
      }
      setError('Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [days, token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const deviceRows = useMemo(() => {
    const d = stats?.devices || {};
    return Object.keys(d)
      .filter((k) => k !== 'bot')
      .map((k) => ({ key: k, label: DEVICE_LABELS[k] || k, count: d[k], leadIcon: DEVICE_ICONS[k] || HardDrive }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  const osRows = useMemo(() => {
    const o = stats?.os || {};
    return Object.keys(o)
      .map((k) => ({ key: k, label: k === 'Other' ? 'Sonstige' : k, count: o[k] }))
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  const countryRows = useMemo(
    () =>
      (stats?.countries || [])
        .slice(0, 10)
        .map((c) => ({ key: c.country, label: `${regionName(c.country)} (${c.country})`, count: c.count })),
    [stats]
  );

  const series = useMemo(
    () =>
      (stats?.series || []).map((s) => ({
        day: s.day.slice(5), // MM-DD
        Seitenaufrufe: s.pageviews,
        Besucher: s.uniqueVisits,
      })),
    [stats]
  );

  const totals = stats?.totals || {};
  const today = stats?.today || {};

  return (
    <div className="min-h-[100dvh] w-full bg-[#FBFBFA] text-foreground" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-3 border-b border-border bg-white/90 px-5 py-3 backdrop-blur">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md text-white" style={{ background: INK }}>
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold leading-tight">SwitchR · Nutzungsmetriken</div>
            <div className="text-[11px] text-muted-foreground">Cookiefrei · anonyme Aggregatdaten</div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="admin-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Letzte 7 Tage</SelectItem>
              <SelectItem value="30">Letzte 30 Tage</SelectItem>
              <SelectItem value="90">Letzte 90 Tage</SelectItem>
              <SelectItem value="365">Letzte 365 Tage</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="admin-refresh-button">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </Button>
          <Button variant="ghost" size="sm" onClick={onLogout} className="h-9 text-muted-foreground" data-testid="admin-logout-button">
            <LogOut className="mr-1.5 h-4 w-4" /> Abmelden
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-6">
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-[#B7791F]/30 bg-[rgba(183,121,31,0.08)] px-3 py-2 text-sm text-[#8a5a12]">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}

        {loading && !stats ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Lade Statistiken…
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <Kpi icon={Users} label="Besucher heute" value={nf(today.uniqueVisits)} testid="kpi-visitors-today" />
              <Kpi icon={Eye} label="Aufrufe heute" value={nf(today.pageviews)} testid="kpi-views-today" />
              <Kpi icon={Users} label="Besucher gesamt" value={nf(totals.uniqueVisits)} sub="Unique/Tag summiert" testid="kpi-visitors-total" />
              <Kpi icon={Eye} label="Aufrufe gesamt" value={nf(totals.pageviews)} testid="kpi-views-total" />
              <Kpi icon={Link2} label="Aktive geteilte Links" value={nf(stats?.activeShares)} sub="derzeit gültig" testid="kpi-active-shares" />
              <Kpi icon={Share2} label="Geteilte Links gesamt" value={nf(totals.sharesCreated)} testid="kpi-shares-total" />
            </section>

            <section className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Kpi icon={FileDown} label="PDF-Exporte (Server) gesamt" value={nf(totals.pdfExports)} testid="kpi-pdf-total" />
            </section>

            {/* Traffic chart */}
            <Card className="mt-5 border-border/70">
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-semibold">Verlauf · Besucher &amp; Seitenaufrufe</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 w-full" data-testid="admin-traffic-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={ACCENT} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0F7B6C" stopOpacity={0.28} />
                          <stop offset="100%" stopColor="#0F7B6C" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAEAE8" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#8A8F98' }} tickLine={false} axisLine={{ stroke: '#EAEAE8' }} minTickGap={16} />
                      <YAxis tick={{ fontSize: 11, fill: '#8A8F98' }} tickLine={false} axisLine={false} allowDecimals={false} width={44} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="Seitenaufrufe" stroke={ACCENT} strokeWidth={2} fill="url(#gViews)" />
                      <Area type="monotone" dataKey="Besucher" stroke="#0F7B6C" strokeWidth={2} fill="url(#gVisitors)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Breakdowns */}
            <section className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <BarList title="Geräteart" icon={Monitor} rows={deviceRows} empty="Noch keine Daten." />
              <BarList title="Betriebssystem" icon={HardDrive} rows={osRows} empty="Noch keine Daten." />
              <BarList title="Herkunftsländer (Top 10)" icon={Globe} rows={countryRows} empty="Noch keine Daten." />
            </section>

            <p className="mt-6 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              DSGVO-freundlich: keine Cookies, keine IP-Speicherung, kein Tracking über Sitzungen hinweg. Herkunftsland via Offline-GeoIP (IP wird nur kurz verarbeitet und verworfen).
              {stats && !stats.geoEnabled && ' · Hinweis: GeoIP-Datenbank nicht geladen.'}
            </p>
          </>
        )}
      </main>
    </div>
  );
};

// ---- Page -------------------------------------------------------------------
export default function AdminMetrics() {
  const [token, setToken] = useState(() => {
    try {
      return sessionStorage.getItem(TOKEN_KEY) || '';
    } catch {
      return '';
    }
  });

  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
    setToken('');
  }, []);

  if (!token) return <LoginScreen onAuthed={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
